/* *
 * Licensed under the Apache License, Version 2.0 (the "License");
 * you may not use this file except in compliance with the License.
 * You may obtain a copy of the License at
 *
 *     http://www.apache.org/licenses/LICENSE-2.0
 *
 * Unless required by applicable law or agreed to in writing, software
 * distributed under the License is distributed on an "AS IS" BASIS,
 * WITHOUT WARRANTIES OR CONDITIONS OF ANY KIND, either express or implied.
 * See the License for the specific language governing permissions and
 * limitations under the License.
 *
 * Copyright 2024 - Koninklijk Nederlands Meteorologisch Instituut (KNMI)
 * Copyright 2024 - Finnish Meteorological Institute (FMI)
 * Copyright 2024 - The Norwegian Meteorological Institute (MET Norway)
 * */

import { isEqual } from 'lodash';
import { useEffect, useRef } from 'react';
import { shallowEqual, useDispatch, useSelector, useStore } from 'react-redux';
import {
  getLinkedMapIds,
  getLinkedTargetIds,
  selectSharedData,
} from './selectors';
import { syncGroupsActions } from './syncGroups';
import { layerActions } from '../map/layer';
import type { CoreAppStore } from '../types';
import {
  getFeatureSyncLayersForPanel,
  upsertFeatureSyncLayer,
} from './syncGroups/featureSyncLayers';
import type { PotentialData } from './syncGroups/types';
import type { FeatureLayer } from '../map/layer/types';

export const useUpdateSharedData = (
  data: PotentialData,
  panelId?: string,
  options?: { requireLinkedMaps?: boolean },
): PotentialData => {
  const resolvedPanelId = panelId ?? '';
  const linkedMaps = useSelector(
    (state: CoreAppStore) =>
      panelId ? getLinkedTargetIds(state, panelId) : [],
    shallowEqual,
  );
  const dispatch = useDispatch();
  const requireLinkedMaps = options?.requireLinkedMaps ?? true;

  const stableDataRef = useRef<PotentialData>(data);
  if (!isEqual(stableDataRef.current, data)) {
    stableDataRef.current = data;
  }
  const stableData = stableDataRef.current;

  const lastDispatchedRef = useRef<{
    panelId: string | undefined;
    linkedMaps: string[];
    data: PotentialData;
  } | null>(null);

  useEffect(() => {
    const canUpdateSharedData = requireLinkedMaps
      ? linkedMaps?.length > 0
      : true;
    if (!panelId || !canUpdateSharedData) {
      return;
    }
    const last = lastDispatchedRef.current;
    if (
      last?.panelId === panelId &&
      shallowEqual(last.linkedMaps, linkedMaps) &&
      isEqual(last.data, stableData)
    ) {
      return;
    }
    lastDispatchedRef.current = { panelId, linkedMaps, data: stableData };

    dispatch(
      syncGroupsActions.addFeatureSyncState({
        panelId,
        data: stableData,
      }),
    );
  }, [panelId, linkedMaps, stableData, dispatch, requireLinkedMaps]);

  useEffect(() => {
    return (): void => {
      if (panelId) {
        const cleanupData = lastDispatchedRef.current?.data;
        lastDispatchedRef.current = null;
        const resetData: PotentialData = {};

        if (cleanupData?.formFeatures !== undefined) {
          resetData.formFeatures = [];
        }

        if (cleanupData?.selectedFeatureId !== undefined) {
          resetData.selectedFeatureId = undefined;
        }

        if (Object.keys(resetData).length > 0) {
          dispatch(
            syncGroupsActions.addFeatureSyncState({
              panelId,
              data: resetData,
            }),
          );
        }
      }
    };
  }, [dispatch, panelId]);
  return useSelector((state: CoreAppStore) =>
    selectSharedData(state, resolvedPanelId),
  );
};

export const useUpdateFeatureSyncLayers = (
  featureLayers: Record<string, FeatureLayer>,
  panelId?: string,
): void => {
  const linkedMapIds = useSelector(
    (state: CoreAppStore) => (panelId ? getLinkedMapIds(state, panelId) : []),
    shallowEqual,
  );
  const dispatch = useDispatch();
  const store = useStore<CoreAppStore>();

  const stableLayersRef = useRef<Record<string, FeatureLayer>>(featureLayers);
  if (!isEqual(stableLayersRef.current, featureLayers)) {
    stableLayersRef.current = featureLayers;
  }
  const stableFeatureLayers = stableLayersRef.current;

  useEffect(() => {
    if (!panelId) {
      return;
    }

    const nextLayerKeys = new Set(Object.keys(stableFeatureLayers));

    Object.entries(stableFeatureLayers).forEach(
      ([featureLayerKey, featureLayerData]) => {
        linkedMapIds.forEach((mapId) => {
          upsertFeatureSyncLayer(
            store.getState().layers,
            dispatch,
            panelId,
            featureLayerKey,
            mapId,
            featureLayerData,
          );
        });
      },
    );

    getFeatureSyncLayersForPanel(store.getState().layers, panelId).forEach(
      ({ layerId, mapId, featureLayerKey }) => {
        if (
          !linkedMapIds.includes(mapId) ||
          !nextLayerKeys.has(featureLayerKey)
        ) {
          dispatch(layerActions.layerDelete({ layerId, mapId }));
        }
      },
    );
  }, [dispatch, linkedMapIds, panelId, stableFeatureLayers, store]);

  useEffect(
    () => (): void => {
      if (!panelId) {
        return;
      }

      getFeatureSyncLayersForPanel(store.getState().layers, panelId).forEach(
        ({ layerId, mapId }) => {
          dispatch(layerActions.layerDelete({ layerId, mapId }));
        },
      );
    },
    [dispatch, panelId, store],
  );
};
